import React from 'react';
import { Box, Paper, Typography, useMediaQuery, useTheme, Skeleton } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';

interface StatsCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  iconBgColor?: string;
  trend?: {
    value: number;
    label?: string;
  };
  loading?: boolean;
  subtitle?: string;
  onClick?: () => void;
}

const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  icon,
  iconBgColor = '#B25373',
  trend,
  loading = false,
  subtitle,
  onClick,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (loading) {
    return (
      <Paper
        sx={{
          p: { xs: 2, sm: 2.5 },
          borderRadius: 2,
          height: '100%',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: 2 }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" height={20} />
            <Skeleton variant="text" width="40%" height={32} sx={{ mt: 0.5 }} />
          </Box>
        </Box>
      </Paper>
    );
  }

  const isPositiveTrend = trend && trend.value >= 0;

  return (
    <Paper
      onClick={onClick}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2,
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        '&:hover': onClick
          ? {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            }
          : {},
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        {/* Icon */}
        {icon && (
          <Box
            sx={{
              width: { xs: 44, sm: 48 },
              height: { xs: 44, sm: 48 },
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: iconBgColor,
              color: 'white',
              flexShrink: 0,
              '& svg': {
                fontSize: { xs: 22, sm: 24 },
              },
            }}
          >
            {icon}
          </Box>
        )}

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontWeight: 500,
              mb: 0.5,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
          </Typography>

          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              lineHeight: 1.2,
              direction: 'ltr',
              textAlign: 'right',
            }}
          >
            {typeof value === 'number' ? value.toLocaleString('fa-IR') : value}
          </Typography>

          {/* Trend or Subtitle */}
          {(trend || subtitle) && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {trend && (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: isPositiveTrend ? 'success.main' : 'error.main',
                    }}
                  >
                    {isPositiveTrend ? (
                      <TrendingUp sx={{ fontSize: 16 }} />
                    ) : (
                      <TrendingDown sx={{ fontSize: 16 }} />
                    )}
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, direction: 'ltr', marginInlineStart: 0.25 }}
                    >
                      {Math.abs(trend.value)}%
                    </Typography>
                  </Box>
                  {trend.label && (
                    <Typography variant="caption" color="text.secondary">
                      {trend.label}
                    </Typography>
                  )}
                </>
              )}
              {subtitle && !trend && (
                <Typography variant="caption" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default StatsCard;
